import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowLeft, Save, X, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { materialsApi, apiClient } from '@/services/api'
import { TemplateDrivenMaterialInfoSection } from '@/components/materials/TemplateDrivenMaterialInfoSection'
import { useMaterialForm } from '@/components/materials/useMaterialForm'
import { useTemplateForm } from '@/hooks/useTemplateForm'
import type { MaterialFormData } from '@/components/materials/types'

export const EditMaterialPage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [material, setMaterial] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const {
    formData,
    setFormData,
    availableUnits,
    errors,
    handleInputChange,
    validateForm
  } = useMaterialForm()

  const {
    formConfig
  } = useTemplateForm()

  useEffect(() => {
    const loadData = async () => {
      try {
        // Load material data
        const materialResponse = await materialsApi.getById(id!)
        
        // Handle different response structures
        const materialData = materialResponse.data || materialResponse
        setMaterial(materialData)
        
        // Populate form with existing material data
        console.log('Material data from API:', materialData) // Debug log
        
        const updatedFormData: MaterialFormData = {
          name: materialData.name || '',
          description: materialData.description || '',
          // Use template-based fields only
          category: materialData.templateCategoryId || '',
          sku: materialData.sku || '',
          baseUnit: materialData.templateUnitType?.name || '',
          brand: materialData.brand || '',
          reorderPoint: materialData.reorderPoint || 0,
          maxStock: materialData.maxStock || 0,
          
          // Specifications - merge from JSONB field and individual fields
          specifications: {
            // Core fields that might be in specifications JSONB or individual fields
            color: materialData.specifications?.color || '',
            gauge: materialData.specifications?.gauge || materialData.size || '',
            material: materialData.specifications?.material || '',
            finish: materialData.specifications?.finish || '',
            diameter: materialData.specifications?.diameter || '',
            length: materialData.specifications?.length || '',
            width: materialData.specifications?.width || '',
            thickness: materialData.specifications?.thickness || '',
            purity: materialData.specifications?.purity || '',
            purityId: materialData.specifications?.purityId || materialData.purityId || '',
            grade: materialData.specifications?.grade || '',
            clarity: materialData.specifications?.clarity || '',
            cut: materialData.specifications?.cut || '',
            shape: materialData.specifications?.shape || '',
            style: materialData.specifications?.style || '',
            coating: materialData.specifications?.coating || '',
            hardness: materialData.specifications?.hardness || '',
            
            // Template-specific fields for different business types
            manufacturer: materialData.specifications?.manufacturer || materialData.brand || '',
            material_type: materialData.specifications?.material_type || '',
            print_temperature: materialData.specifications?.print_temperature || '',
            bed_temperature: materialData.specifications?.bed_temperature || '',
            layer_height: materialData.specifications?.layer_height || '',
            shore_hardness: materialData.specifications?.shore_hardness || '',
            species: materialData.specifications?.species || '',
            moisture: materialData.specifications?.moisture || '',
            format: materialData.specifications?.format || '',
            iso: materialData.specifications?.iso || '',
            
            // Ensure all existing specifications are preserved
            ...(materialData.specifications || {})
          },
          
          // Purchase information (disabled for edit mode)
          includePurchase: false,
          purchaseQuantity: 0,
          purchaseUnit: materialData.templateUnitType?.name || '',
          convertedQuantity: 0,
          costPerUnit: materialData.lastCostPerUnit || materialData.averageCost || 0,
          totalCost: 0,
          costInputType: 'per-unit' as const,
          
          // Package purchasing
          isPackagePurchase: false,
          packageQuantity: 1,
          itemsPerPackage: 1,
          packageCost: 0,
          
          supplier: materialData.preferredSupplier || '',
          supplierId: '',
          purchaseDate: new Date().toISOString().split('T')[0],
          invoiceNumber: '',
          notes: materialData.notes || ''
        }
        
        console.log('Mapped form data:', updatedFormData) // Debug log
        setFormData(updatedFormData)
      } catch (error: any) {
        console.error('Error loading data:', error)
        if (error.response?.status === 404) {
          setError('Material not found')
        } else {
          setError(error instanceof Error ? error.message : 'Failed to load material')
        }
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      loadData()
    }
  }, [id, setFormData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      setError(`Please fix the following errors: ${Object.values(errors).join(', ')}`)
      return
    }
    
    setSaving(true)
    setError(null)

    try {
      // Resolve unit name to UUID
      let templateUnitTypeId = formData.baseUnit
      if (formConfig && formData.baseUnit) {
        const unitType = formConfig.unitTypes.find(unit => unit.name === formData.baseUnit)
        if (unitType) {
          templateUnitTypeId = unitType.id
        }
      }

      const updateData: any = {
        name: formData.name,
        description: formData.description,
        templateCategoryId: formData.category,
        templateUnitTypeId: templateUnitTypeId,
        sku: formData.sku,
        brand: formData.specifications?.manufacturer || formData.brand,
        reorderPoint: Number(formData.reorderPoint) || 0,
        maxStock: Number(formData.maxStock) || 0,
        specifications: formData.specifications,
        notes: formData.notes
      }

      // Don't send top-level color or purityId - they should only be in specifications
      // The DTO likely doesn't expect these as top-level fields

      console.log('Sending update data:', updateData) // Debug log

      await materialsApi.update(id!, updateData)

      // Navigate back to materials list
      navigate('/materials')
    } catch (error) {
      console.error('Error updating material:', error)
      setError(error instanceof Error ? error.message : 'Failed to update material')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this material? This action cannot be undone.')) {
      return
    }

    setDeleting(true)
    setError(null)

    try {
      await materialsApi.delete(id!)

      // Navigate back to materials list
      navigate('/materials')
    } catch (error) {
      console.error('Error deleting material:', error)
      setError(error instanceof Error ? error.message : 'Failed to delete material')
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !material) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {error === 'Material not found' ? 'Material Not Found' : 'Error Loading Material'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {error || 'The material you\'re looking for doesn\'t exist or has been deleted.'}
        </p>
        <Button onClick={() => navigate('/materials')}>
          Back to Materials
        </Button>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Edit Material - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/materials')}
              leftIcon={<ArrowLeft className="w-4 h-4" />}
            >
              Back to Materials
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Edit Material
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Update material information and inventory details
              </p>
            </div>
          </div>
          
          <Button
            variant="outline"
            onClick={handleDelete}
            loading={deleting}
            leftIcon={<Trash2 className="w-4 h-4" />}
            className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-600 dark:hover:bg-red-900/20"
          >
            Delete Material
          </Button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Template-Driven Material Information Section */}
          <TemplateDrivenMaterialInfoSection
            formData={formData}
            onInputChange={handleInputChange}
            errors={errors}
          />

          {/* Inventory Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Inventory Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Reorder Point */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reorder Point
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.reorderPoint}
                  onChange={(e) => handleInputChange('reorderPoint', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder="0"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Alert when stock falls below this level
                </p>
              </div>

              {/* Maximum Stock */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Maximum Stock
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.maxStock}
                  onChange={(e) => handleInputChange('maxStock', parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                  placeholder="0"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Maximum stock level for inventory management
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Notes
              </label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                placeholder="Any additional notes about this material..."
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row gap-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/materials')}
              leftIcon={<X className="w-4 h-4" />}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saving}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Save Changes
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}